import { Helmet } from 'react-helmet-async';

const defaultMeta = {
  title: "Carpe Koi & Nishikigoi : Guide Complet des Koi Japonais",
  description: "Tout savoir sur les carpes koi et nishikigoi. Guide complet : 100+ varietes de koi, eleveurs japonais de Niigata, soins du bassin, alimentation, prix. La reference francaise.",
  image: "https://nishikigoi.fr/og-image.jpg",
  url: "https://nishikigoi.fr",
  type: "website",
  keywords: "carpe koi, nishikigoi, koi japonais, bassin koi, eleveurs koi, varietes koi, kohaku, sanke, showa, tancho, soins koi, alimentation koi"
};

export const SEO = ({ 
  title, 
  description, 
  image, 
  url,
  type = "website",
  article = null,
  noindex = false,
  structuredData = null,
  keywords = null
}) => {
  const pageTitle = title 
    ? `${title} | Nishikigoi.fr` 
    : defaultMeta.title;
  const pageDescription = description || defaultMeta.description;
  const pageImage = image || defaultMeta.image;
  const pageUrl = url ? `https://nishikigoi.fr${url}` : defaultMeta.url;
  const pageKeywords = keywords || defaultMeta.keywords;

  // Handle multiple structured data schemas (array or single object)
  const renderStructuredData = () => {
    if (!structuredData) return null;
    
    // If it's an array, render each schema separately
    if (Array.isArray(structuredData)) {
      return structuredData.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ));
    }
    
    // Single schema object
    return (
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    );
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="title" content={pageTitle} />
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <link rel="canonical" href={pageUrl} />
      
      {/* Language and Region */}
      <html lang="fr" />
      <meta name="language" content="French" />
      <meta name="geo.region" content="FR" />
      <meta name="geo.placename" content="France" />
      
      {/* AI Crawler Hints - LLMO Optimization */}
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} />
      <meta name="googlebot" content="index, follow" />
      <meta name="bingbot" content="index, follow" />
      
      {/* Content Classification for AI */}
      <meta name="classification" content="Animaux, Poissons, Carpes Koi, Nishikigoi, Aquariophilie" />
      <meta name="category" content="Aquarium & Pond Fish" />
      <meta name="topic" content="Japanese Koi Carp (Nishikigoi)" />
      <meta name="subject" content={pageTitle} />
      <meta name="coverage" content="Worldwide" />
      <meta name="distribution" content="Global" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:locale" content="fr_FR" />
      <meta property="og:site_name" content="Nishikigoi.fr - Carpe Koi" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={pageUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />
      
      {/* Article specific */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          <meta property="article:modified_time" content={article.modifiedTime} />
          <meta property="article:author" content={article.author || "Nishikigoi.fr"} />
          <meta property="article:section" content={article.section || "Carpes Koi"} />
          {article.tags?.map((tag, i) => (
            <meta key={i} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* JSON-LD Structured Data */}
      {renderStructuredData()}
    </Helmet>
  );
};

// Predefined SEO configs for main pages - Optimized for high-traffic keywords
export const pageSEO = {
  home: {
    title: null, // Uses default
    description: "Tout sur les carpes koï et nishikigoi : 100+ variétés, éleveurs japonais de Niigata, soins, alimentation, prix. La référence française du koï japonais.",
    url: "/"
  },
  varieties: {
    title: "Variétés de Koï - Encyclopédie des 100+ Variétés de Carpes Koï",
    description: "Découvrez toutes les variétés de carpes koï : Kohaku, Sanke, Showa, Tancho, Ogon, Chagoi, Asagi et 100+ autres. Photos, descriptions, origines et prix de chaque variété de nishikigoi.",
    url: "/varietes"
  },
  breeders: {
    title: "Éleveurs de Koï Japonais - Les Meilleurs Éleveurs de Niigata",
    description: "Les meilleurs éleveurs de carpes koï au Japon : Dainichi, Sakai, Omosako, Maruhiro. Carte interactive de Niigata, histoire des fermes et où acheter des koï de qualité.",
    url: "/eleveurs"
  },
  history: {
    title: "Histoire des Carpes Koï - Origines et Culture du Nishikigoi",
    description: "L'histoire fascinante des carpes koï : de la Chine ancienne au Japon, comment le nishikigoi est devenu le poisson le plus précieux au monde. Culture, traditions et symbolisme.",
    url: "/histoire"
  },
  care: {
    title: "Soins des Koï - Maladies, Traitements et Entretien du Bassin",
    description: "Guide complet des soins pour carpes koï : maladies courantes (points blancs, ulcères), traitements, qualité de l'eau, quarantaine. Tout pour garder vos koï en bonne santé.",
    url: "/soins"
  },
  food: {
    title: "Alimentation des Koï - Nourriture et Guide Saisonnier",
    description: "Quelle nourriture pour vos carpes koï ? Guide complet : alimentation selon la saison et température, fréquence des repas, aliments pour couleurs et croissance optimales.",
    url: "/alimentation"
  },
  contests: {
    title: "Concours de Koï - Calendrier, Champions et Critères de Jugement",
    description: "Calendrier des concours de carpes koï en France et au Japon. Découvrez les champions, le Hall of Fame, les critères de jugement et comment participer aux expositions.",
    url: "/concours"
  },
  faq: {
    title: "FAQ Koï - Questions Fréquentes sur les Carpes Koï",
    description: "Toutes les réponses sur les carpes koï : prix, taille de bassin, alimentation, maladies, variétés pour débutants. La FAQ la plus complète sur les nishikigoi.",
    url: "/faq"
  },
  lexicon: {
    title: "Lexique Koï - Termes Japonais et Vocabulaire du Nishikigoi",
    description: "Dictionnaire complet des termes japonais du koï : couleurs (aka, shiro, ki), motifs (sumi, hi), qualités (Tategoi, Tateshita). Apprenez le vocabulaire des carpes koï.",
    url: "/lexique"
  },
  blog: {
    title: "Blog Koï - Actualités et Conseils sur les Carpes Koï",
    description: "Articles et conseils d'experts sur les carpes koï : soins, alimentation, variétés, bassins. Restez informé des dernières actualités du monde du nishikigoi.",
    url: "/blog"
  },
  gallery: {
    title: "Galerie Photos de Koï - Les Plus Beaux Nishikigoi",
    description: "Galerie de photos des plus belles carpes koï. Admirez des Kohaku, Sanke, Showa et autres variétés exceptionnelles. Partagez vos propres photos de nishikigoi.",
    url: "/galerie"
  },
  quiz: {
    title: "Quiz Koï - Quel Koï Êtes-Vous ? Test de Personnalité",
    description: "Découvrez quelle variété de carpe koï correspond à votre personnalité ! Quiz interactif + test de connaissances sur les nishikigoi, variétés et vocabulaire japonais.",
    url: "/quiz"
  },
  contact: {
    title: "Contact - Nishikigoi.fr",
    description: "Contactez l'équipe Nishikigoi.fr pour vos questions sur les carpes koï, conseils personnalisés ou partenariats.",
    url: "/contact"
  },
  favorites: {
    title: "Mes Favoris - Variétés de Koï Sauvegardées",
    description: "Retrouvez toutes les variétés de carpes koï que vous avez sauvegardées dans vos favoris.",
    url: "/favoris"
  },
  simulator: {
    title: "Simulateur de Bassin Koï - Planifiez Votre Bassin Idéal",
    description: "Concevez votre bassin à carpes koï avec notre simulateur interactif. Calculez le volume, le nombre de koï, choisissez la filtration. Export PDF gratuit de votre plan.",
    url: "/simulateur"
  }
};

export default SEO;
